import {
  pullRequestWorkspaceRowKey,
  type PullRequestWorkspaceView,
  type EnvironmentPullRequestRow,
} from "@afar-research/client-runtime/state/pull-requests";
import type { EnvironmentId } from "@afar-research/contracts";
import { GitPullRequestArrowIcon } from "lucide-react";

import { cn } from "../lib/utils";
import { showPullRequestLinkContextMenu } from "./pullRequest/pullRequestLinkContextMenu";
import { PullRequestChecksPopover } from "./pullRequest/PullRequestChecksPopover";
import { pullRequestListChainLines } from "./pullRequest/pullRequestListChains.logic";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { pullRequestSurfaceId } from "../pullRequestSurfaceStore";
import { PullRequestListEmptyState } from "./pullRequest/PullRequestListEmptyState";
import { PullRequestsUnavailableState } from "./pullRequest/PullRequestsUnavailableState";
import { PullRequestListGhost } from "./pullRequest/PullRequestGhosts";
import {
  pullRequestLabelColor,
  PullRequestActorLabel,
  PullRequestApprovalGlyph,
  PullRequestChecksGlyph,
  PullRequestDiffStat,
  PullRequestMetaLine,
  PullRequestStateGlyph,
} from "./pullRequest/pullRequestPresentation";
import { Button } from "./ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { WorkspaceShellStatus } from "./WorkspaceShellPrimitives";

const EMPTY_ENVIRONMENT_LABELS: ReadonlyMap<EnvironmentId, string> = new Map();
const EMPTY_CHECKS_READABLE: ReadonlySet<EnvironmentId> = new Set();

/**
 * Following T3Code: each slot past the first only appears once the meta line is wide enough to
 * hold it, so a narrow row shows one label and a "+N" while a wide one spreads out up to three.
 * The "+N" rides whichever pill is last visible, and goes as soon as the next slot shows.
 */
const LABEL_SLOTS = [
  { pill: "", overflow: "@xl/pr-row-meta:hidden" },
  { pill: "hidden @xl/pr-row-meta:inline-flex", overflow: "@3xl/pr-row-meta:hidden" },
  { pill: "hidden @3xl/pr-row-meta:inline-flex", overflow: "" },
] as const;

function PullRequestRowLabels({
  labels,
}: {
  readonly labels: EnvironmentPullRequestRow["labels"];
}) {
  if (labels.length === 0) return null;
  return (
    <span className="flex min-w-0 items-center gap-1" data-pull-request-labels="">
      {LABEL_SLOTS.map((slot, index) => {
        const label = labels[index];
        if (label === undefined) return null;
        const dot = pullRequestLabelColor(label.color);
        const remaining = labels.length - index - 1;
        return (
          <span
            className={cn(
              "inline-flex min-w-0 max-w-40 items-center gap-1 rounded-full border border-border/70 bg-muted/40 py-0 pl-1 pr-1.5 text-[10px] leading-3.5 text-muted-foreground",
              slot.pill,
            )}
            key={label.name}
          >
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full bg-muted-foreground"
              {...(dot === null ? {} : { style: { backgroundColor: dot } })}
            />
            <span className="truncate">{label.name}</span>
            {remaining > 0 ? (
              <span className={cn("shrink-0", slot.overflow)}>+{remaining}</span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

export function PullRequestList({
  environmentLabels = EMPTY_ENVIRONMENT_LABELS,
  onRetry,
  query = "",
  onClearQuery,
  checksReadableEnvironments = EMPTY_CHECKS_READABLE,
  onOpenDetails,
  selectedSurfaceId = null,
  view,
}: {
  readonly environmentLabels?: ReadonlyMap<EnvironmentId, string>;
  readonly onRetry: () => void;
  /** What the workspace is searching for, so an empty list can say what was searched. */
  readonly query?: string;
  readonly onClearQuery?: (() => void) | undefined;
  /** The Environments that can read a row's own checks, so the glyph only opens where it can. */
  readonly checksReadableEnvironments?: ReadonlySet<EnvironmentId>;
  readonly onOpenDetails?: (pullRequest: EnvironmentPullRequestRow) => void;
  /** The detail the adjacent panel is showing, so the list can say which row it belongs to. */
  readonly selectedSurfaceId?: string | null;
  readonly view: PullRequestWorkspaceView;
}) {
  if (view.phase === "loading") {
    return (
      // The ghost is the live region; the shell would otherwise announce a second, unnamed one.
      <WorkspaceShellStatus className="px-1 py-2" kind="loading" role="presentation">
        {/* Rows in the shape of the rows that are coming, rather than a spinner that says only
            that something is happening. One pulse on the container, so a full page of bars costs
            one opacity animation. */}
        <PullRequestListGhost />
      </WorkspaceShellStatus>
    );
  }
  if (view.phase === "unsupported") {
    return (
      <WorkspaceShellStatus className="min-h-[45vh]" kind="empty">
        <PullRequestsUnavailableState
          error={
            view.failures[0]?.failure.message ??
            "No connected Environment can read Pull Requests. Configure a supported GitHub remote, then retry."
          }
          onRetry={onRetry}
          title="Pull Requests unavailable"
        />
      </WorkspaceShellStatus>
    );
  }
  if (view.phase === "total-failure") {
    return (
      <WorkspaceShellStatus className="min-h-[45vh]" kind="error">
        <PullRequestsUnavailableState
          error={view.failures[0]?.failure.message ?? "No Environment returned a usable partition."}
          onRetry={onRetry}
          role="alert"
          title="Pull Requests could not load"
        />
      </WorkspaceShellStatus>
    );
  }
  if (view.phase === "true-empty" || view.phase === "filtered-empty") {
    return (
      <WorkspaceShellStatus className="min-h-[45vh]" kind="empty">
        {/* The view model already separates an empty workspace from one narrowed to nothing,
            so this reads that verdict rather than re-deriving it from the filters. */}
        <PullRequestListEmptyState
          filtered={view.phase === "filtered-empty"}
          onClearQuery={onClearQuery}
          onRefresh={onRetry}
          query={view.phase === "filtered-empty" ? query : ""}
        />
      </WorkspaceShellStatus>
    );
  }

  // One line for however many Environments could not answer: each says its own thing once,
  // and a workspace spanning several of them does not stack a paragraph per Environment.
  const failureMessage =
    view.phase === "partial-failure" || view.phase === "retained-refresh-failure"
      ? [...new Set(view.failures.map((failure) => failure.failure.message))].join(" ")
      : null;
  return (
    <div className="grid gap-3">
      {/* Following T3Code, the rows sit on the page rather than inside a titled card: the page
          is already called Pull Requests, and a card around them says it a second time while
          taking a border's worth of width from every row. */}
      <div>
        {view.statisticsPending ? (
          <div className="px-3 pb-1 text-xs text-muted-foreground">Loading statistics…</div>
        ) : null}
        <ul aria-label="Workspace Pull Requests" className="grid">
          {pullRequestListChainLines(view.rows).map(({ row: pullRequest, depth, chainSize }) => {
            const surfaceId = pullRequestSurfaceId({
              environmentId: pullRequest.environmentId,
              projectId: pullRequest.projectId,
              repository: pullRequest.repository,
              number: pullRequest.number,
              url: pullRequest.url,
            });
            const selected = surfaceId === selectedSurfaceId;
            const checksReadable = checksReadableEnvironments.has(pullRequest.environmentId);
            const openable = pullRequest.canOpenDetails && onOpenDetails !== undefined;
            return (
              <li
                data-authority={pullRequest.authority}
                data-can-open-details={String(pullRequest.canOpenDetails)}
                data-pull-request-row=""
                data-selected={String(selected)}
                key={pullRequestWorkspaceRowKey(pullRequest)}
              >
                {/* The row is the control, so pointer and keyboard reach the detail through
                    exactly the same path. A row whose Environment cannot open a detail stays
                    a plain row rather than a button that would do nothing. */}
                <div
                  aria-current={selected ? "true" : undefined}
                  aria-label={openable ? `Open pull request #${pullRequest.number}` : undefined}
                  role={openable ? "button" : undefined}
                  tabIndex={openable ? 0 : undefined}
                  onClick={openable ? () => onOpenDetails?.(pullRequest) : undefined}
                  onKeyDown={
                    openable
                      ? (event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          onOpenDetails?.(pullRequest);
                        }
                      : undefined
                  }
                  // Each layer steps in under the one it is built on. The step is capped: past a
                  // few layers the indent only says "still in the chain", and a long one would
                  // otherwise stair-step off the right edge.
                  style={{ paddingLeft: `${0.75 + Math.min(depth, 3) * 1.25}rem` }}
                  className={cn(
                    "@container/pr-row grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-lg py-3 pr-3 text-left transition-colors",
                    // Offscreen rows are skipped for style, layout and paint, so a long list
                    // costs what the viewport shows rather than what has been loaded. The
                    // intrinsic size keeps the scrollbar honest while a row is skipped.
                    "[contain-intrinsic-block-size:66px] [content-visibility:auto]",
                    openable && "cursor-pointer",
                    selected ? "bg-accent" : openable && "hover:bg-accent/60",
                  )}
                >
                  <span className="flex shrink-0 items-center gap-1.5">
                    {depth > 0 ? (
                      <span aria-hidden className="-ml-2 h-6 w-px shrink-0 bg-border/70" />
                    ) : null}
                    <PullRequestStateGlyph
                      state={pullRequest.state}
                      isDraft={pullRequest.isDraft}
                    />
                  </span>
                  <span className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5">
                    <span className="col-start-1 row-start-1 block truncate text-sm font-medium text-foreground">
                      {pullRequest.title}
                    </span>
                    <span className="col-start-2 row-start-1 flex items-center gap-2 justify-self-end text-xs">
                      {pullRequest.authority !== "live" ? (
                        <span className="rounded bg-warning/15 px-1.5 py-0.5 font-medium text-warning-foreground">
                          {pullRequest.authority === "stale"
                            ? "Read-only stale snapshot"
                            : "Retained after refresh failure"}
                        </span>
                      ) : null}
                      {pullRequest.reviewDecision === "approved" ? (
                        <PullRequestApprovalGlyph />
                      ) : null}
                      {pullRequest.reviewDecision === "changes-requested" ? (
                        <span className="text-amber-600/90 dark:text-amber-400/80">
                          Changes requested
                        </span>
                      ) : null}
                      {pullRequest.checksState === undefined ? null : checksReadable ? (
                        <PullRequestChecksPopover
                          environmentId={pullRequest.environmentId}
                          host={pullRequest.host}
                          number={pullRequest.number}
                          projectId={pullRequest.projectId}
                          repository={pullRequest.repository}
                          state={pullRequest.checksState}
                        />
                      ) : (
                        // An Environment that cannot read the runs still reports the rollup, so
                        // the glyph stays and simply does not open.
                        <PullRequestChecksGlyph state={pullRequest.checksState} />
                      )}
                      {/* Line counts arrive separately from the listing, so "…" says they
                            are still on the way rather than that nothing changed. */}
                      {pullRequest.additions === null || pullRequest.deletions === null ? (
                        <span className="font-mono text-muted-foreground">…</span>
                      ) : (
                        <PullRequestDiffStat
                          additions={pullRequest.additions}
                          deletions={pullRequest.deletions}
                          className="font-mono"
                        />
                      )}
                    </span>
                    <PullRequestMetaLine className="@container/pr-row-meta col-start-1 row-start-2 overflow-hidden text-xs text-muted-foreground/70">
                      {/* The number carries the link: a right-click on it copies the pull
                          request's own address rather than opening the row. */}
                      <span
                        className="shrink-0 font-mono tabular-nums"
                        onContextMenu={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void showPullRequestLinkContextMenu({
                            url: pullRequest.url,
                            position: { x: event.clientX, y: event.clientY },
                          });
                        }}
                      >
                        {pullRequest.repository}#{pullRequest.number}
                      </span>
                      <span className="truncate">
                        {environmentLabels.get(pullRequest.environmentId) ??
                          pullRequest.environmentId}{" "}
                        · {pullRequest.projectTitle}
                      </span>
                      {pullRequest.author ? (
                        <PullRequestActorLabel
                          actor={pullRequest.author}
                          className="min-w-4 max-w-40"
                          labelClassName="sr-only @xs/pr-row-meta:not-sr-only @xs/pr-row-meta:truncate"
                        />
                      ) : null}
                      <PullRequestRowLabels labels={pullRequest.labels} />
                      {/* Named on the layer that can speak for the whole chain, which is the one
                          the indent hangs from. */}
                      {chainSize === null ? null : (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <span className="inline-flex shrink-0 items-center gap-0.5 text-foreground/70" />
                            }
                          >
                            <GitPullRequestArrowIcon aria-hidden className="size-3" />
                            {chainSize}
                          </TooltipTrigger>
                          <TooltipPopup>
                            {chainSize} pull requests chained by base branch.
                          </TooltipPopup>
                        </Tooltip>
                      )}
                    </PullRequestMetaLine>
                    <span className="col-start-2 row-start-2 justify-self-end whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
                      <span className="hidden @sm/pr-row:inline">
                        {formatRelativeTimeLabel(pullRequest.updatedAt)}
                      </span>
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {/* What the Environment actually said, then what is on screen despite it — following
          T3Code, which prints the read's own error rather than a sentence of its own. */}
      {failureMessage === null ? null : (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
          <span>{failureMessage} Showing the last Pull Requests loaded.</span>
          <Button onClick={onRetry} size="xs" variant="outline">
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}
